import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase, isDemoMode } from '../lib/supabase/client';
import Toast, { ToastMessage } from '../components/Toast';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Notification {
  id: string;
  benutzer_id: string;
  modul: string;
  typ: string;
  nachricht: string;
  referenz_id: string | null;
  referenz_typ: string | null;
  link_pfad: string | null;
  gelesen: boolean;
  erstellt_von: string | null;
  created_at: string;
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  unreadByModul: Record<string, number>;
  markAsRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  showToast: (toast: Omit<ToastMessage, 'id'>) => void;
  refetch: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

/** Verpasste Anrufe legt der Telefonie-Trigger je Mitarbeiter als Benachrichtigung an
 *  (supabase/sql/telefonie-trigger.sql) – bei dem Anrufvolumen überfluteten sie die
 *  Meldungen (#530). Sie stehen stattdessen in der Telefon-App (Anrufliste) und im
 *  Telefon-Widget der Pille (useVerpassteAnrufe.ts), hier werden sie ausgeblendet. */
export const AUSGEBLENDETE_TYPEN = ['anruf_verpasst'];

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useNotification = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    // Return a safe fallback when used outside provider
    return {
      notifications: [],
      unreadCount: 0,
      unreadByModul: {},
      markAsRead: async () => {},
      markAllRead: async () => {},
      showToast: () => {},
      refetch: async () => {},
    };
  }
  return ctx;
};

// ─── Provider ─────────────────────────────────────────────────────────────────

interface NotificationProviderProps {
  userId: string | null;
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ userId, children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const toastTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // ── Fetch initial notifications ──────────────────────────────────────

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;

    let query = supabase
      .from('benachrichtigungen')
      .select('*');

    // Im Demo-Modus: alle Demo-Benachrichtigungen laden (keine User-ID-Filterung)
    if (!isDemoMode()) {
      query = query.eq('benutzer_id', userId);
    }
    query = query.not('typ', 'in', `(${AUSGEBLENDETE_TYPEN.join(',')})`);

    const { data, error } = await query
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error && data) {
      setNotifications(data as Notification[]);
    }
  }, [userId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // ── Supabase Realtime subscription ───────────────────────────────────

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('user-notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'benachrichtigungen',
        filter: `benutzer_id=eq.${userId}`,
      }, (payload) => {
        const newNotif = payload.new as Notification;
        if (AUSGEBLENDETE_TYPEN.includes(newNotif.typ)) return;
        setNotifications(prev => [newNotif, ...prev.slice(0, 49)]);

        // Auto-show toast for new realtime notification
        showToast({
          type: 'info',
          message: newNotif.nachricht,
          modul: newNotif.modul,
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // ── Computed values ──────────────────────────────────────────────────

  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.gelesen).length;
  }, [notifications]);

  const unreadByModul = useMemo(() => {
    const counts: Record<string, number> = {};
    notifications.filter(n => !n.gelesen).forEach(n => {
      counts[n.modul] = (counts[n.modul] || 0) + 1;
    });
    return counts;
  }, [notifications]);

  // ── Actions ──────────────────────────────────────────────────────────

  const markAsRead = useCallback(async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, gelesen: true } : n));
    await supabase.from('benachrichtigungen').update({ gelesen: true }).eq('id', id);
  }, []);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    setNotifications(prev => prev.map(n => ({ ...n, gelesen: true })));
    await supabase
      .from('benachrichtigungen')
      .update({ gelesen: true })
      .eq('benutzer_id', userId)
      .eq('gelesen', false);
  }, [userId]);

  // ── Toast management ─────────────────────────────────────────────────

  const showToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = crypto.randomUUID();
    const newToast: ToastMessage = { ...toast, id };
    setToasts(prev => [newToast, ...prev].slice(0, 5));

    const timeout = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      toastTimeoutsRef.current.delete(id);
    }, 4000);

    toastTimeoutsRef.current.set(id, timeout);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timeout = toastTimeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      toastTimeoutsRef.current.delete(id);
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      toastTimeoutsRef.current.forEach(t => clearTimeout(t));
    };
  }, []);

  // ── Context value ────────────────────────────────────────────────────

  const value = useMemo<NotificationContextValue>(() => ({
    notifications,
    unreadCount,
    unreadByModul,
    markAsRead,
    markAllRead,
    showToast,
    refetch: fetchNotifications,
  }), [notifications, unreadCount, unreadByModul, markAsRead, markAllRead, showToast, fetchNotifications]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Toast toasts={toasts} onDismiss={dismissToast} />
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
